import { RefObject, useEffect, useRef, useState } from 'react';

const TAB_SELECTOR = '.gc-dg-tab-link';
const SUBMIT_SELECTOR = '#gc-dg-payNow';
// Keys the SDK uses to move between payment method tabs
const TAB_SELECTION_KEYS = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];

/**
 * @description The SDK always opens with Bank Account selected, so the layout shows no method as
 * selected and blocks the CTA until the customer picks one.
 * @returns whether a method has been picked, and whether the CTA was used before that
 */
export const usePaymentMethodSelection = (
  panelRef: RefObject<HTMLElement | null>,
  onBlockedSubmit: () => void,
) => {
  const [hasSelectedMethod, setHasSelectedMethod] = useState(false);
  const [isSelectionRequired, setIsSelectionRequired] = useState(false);
  const onBlockedSubmitRef = useRef(onBlockedSubmit);
  onBlockedSubmitRef.current = onBlockedSubmit;

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel || hasSelectedMethod) return;

    const selectMethod = () => {
      setHasSelectedMethod(true);
      setIsSelectionRequired(false);
    };

    // Capture phase, so a blocked CTA click never reaches the SDK submit handler
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Element;
      if (target.closest(TAB_SELECTOR)) {
        selectMethod();
      } else if (target.closest(SUBMIT_SELECTOR)) {
        event.preventDefault();
        event.stopPropagation();
        setIsSelectionRequired(true);
        onBlockedSubmitRef.current();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as Element;
      if (target.closest(TAB_SELECTOR) && TAB_SELECTION_KEYS.includes(event.key)) selectMethod();
    };

    panel.addEventListener('click', handleClick, true);
    panel.addEventListener('keydown', handleKeyDown, true);

    return () => {
      panel.removeEventListener('click', handleClick, true);
      panel.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [panelRef, hasSelectedMethod]);

  return { hasSelectedMethod, isSelectionRequired };
};
